import React from 'react';
import { motion } from 'framer-motion';
import { Play, Info } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../../../utils/animations';

interface NetflixHeroProps {
  title: string;
  subtitle: string;
  description?: string;
  backgroundImage?: string;
  videoUrl?: string;
  buttonText?: string;
  buttonLink?: string;
  height?: string;
}

export const NetflixHero: React.FC<NetflixHeroProps> = ({
  title = "Featured Content",
  subtitle = "Trending Now",
  description = "Dive into our curated selection of premium content designed to enhance your learning experience.",
  backgroundImage,
  videoUrl,
  buttonText = "Watch Now",
  buttonLink = "#",
  height = "600px"
}) => {
  return (
    <motion.div
      className="netflix-hero"
      style={{ minHeight: height }}
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Background Media */}
      {videoUrl ? (
        <video
          autoPlay
          muted
          loop
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      ) : backgroundImage ? (
        <motion.img
          src={backgroundImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      ) : (
        <div className="absolute inset-0 animated-gradient-bg" />
      )}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

      {/* Content */}
      <div className="netflix-hero-content relative z-10 flex items-end h-full pb-20">
        <motion.div className="max-w-3xl" variants={fadeInUp}>
          <motion.div
            className="text-sm uppercase tracking-wider text-gray-300 mb-2"
            variants={fadeInUp}
          >
            {subtitle}
          </motion.div>

          <motion.h1
            className="netflix-hero-title"
            variants={fadeInUp}
          >
            {title}
          </motion.h1>

          {description && (
            <motion.p
              className="netflix-hero-description"
              variants={fadeInUp}
            >
              {description}
            </motion.p>
          )}

          <motion.div
            className="flex gap-4 mt-6"
            variants={fadeInUp}
          >
            <motion.button
              className="netflix-button netflix-button-play"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = buttonLink}
            >
              <Play className="w-6 h-6" fill="currentColor" />
              {buttonText}
            </motion.button>

            <motion.button
              className="netflix-button netflix-button-info"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Info className="w-6 h-6" />
              More Info
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};